import { useMutation } from '@apollo/client'
import { SnackbarVariant } from '@focaldata/cin-ui-components'
import { SET_SCREENING_RULES_V2 } from '../../data/gql/questionnaire/mutations/screeningRulesV2'
import { draftQuestionnaireRefetchQuery } from '../../data/gql/questionnaire/queries'
import { draftAudienceRefetchQuery } from '../../data/gql/questionnaire/queries/audiences'
import { GET_SCREENING_RULES } from '../../data/gql/questionnaire/queries/screeningRules'
import { LoggerErrorType } from '../../data/logger'
import { ScreeningRuleDraft } from '../../data/model/screeningRules'
import { buildCondition } from '../../modules/Audience/ScreeningRules/screeningRules.utils'
import { captureApolloError } from '../../utils/HelperFunctions'
import { useOptionalWaveId } from '../useOptionalWaveId'
import { useProjectId } from '../useProjectId'
import { useSafeEnqueueSnackbar } from '../useSafeEnqueueSnackbar'
import { useSurveyId } from '../useSurveyId'

/**
 * Replaces the whole screening-rule list for this market's screener.
 *
 * The backend rejects a rule that references a question outside this market's audience, an option that is
 * not on that question, or a question that is never asked in-survey; those come back as a plain mutation
 * error, so surface the message rather than swallowing it.
 */
export const useSetScreeningRulesV2 = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const enqueueSnackbar = useSafeEnqueueSnackbar()

  const [setScreeningRulesMutation] = useMutation(SET_SCREENING_RULES_V2, {
    context: { clientName: 'questionnaire' },
    refetchQueries: [
      {
        query: GET_SCREENING_RULES,
        context: { clientName: 'questionnaire' },
        variables: { projectId, surveyId, waveId }
      },
      draftAudienceRefetchQuery(projectId, surveyId, waveId),
      draftQuestionnaireRefetchQuery(projectId, surveyId, waveId)
    ],
    onError(error) {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'SetScreeningRulesV2',
        error
      )
      enqueueSnackbar?.(`Couldn't save screening rules. ${error.message}`, {
        variant: SnackbarVariant.Error
      })
    }
  })

  const setScreeningRules = (rules: ScreeningRuleDraft[]) =>
    setScreeningRulesMutation({
      variables: {
        input: {
          projectId,
          surveyId,
          waveId,
          rules: rules.map((rule) => {
            // Saving replaces the whole list, so a rule the researcher did not touch is re-sent exactly as
            // it was loaded. Re-serialising it from rows would rewrite the stored form of every other rule
            // whenever one is edited — and would change the meaning of any the row editor cannot represent.
            const condition =
              !rule.edited && rule.originalCondition
                ? rule.originalCondition.map((clause) => ({
                    propositions: clause.map((proposition) => ({
                      audienceReponseOption: proposition.proposition,
                      negated: proposition.negated
                    }))
                  }))
                : buildCondition(rule.rows, surveyId).map((clause) => ({
                    propositions: clause.map((proposition) => ({
                      audienceReponseOption: proposition.proposition,
                      negated: proposition.negated
                    }))
                  }))

            return { condition }
          })
        }
      }
    })

  return setScreeningRules
}
